import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import Card from "../../Components/Card";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import SetAvailabilityModal from "../../Components/SetAvailabilityModal";
import { useAuth } from "../../Context/authContext";
import Avatar from "../../Components/Avatar";
import { getAvailabilityByUserId } from "../../Utils/apiServices";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [h, m] = String(timeStr).split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m || 0).padStart(2, "0")} ${period}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
};

const Component = () => {
  const { user } = useAuth();
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityList, setAvailabilityList] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const isMentor = user?.role === "mentor";

  const fetchAvailability = useCallback(async () => {
    if (!isMentor || !user?.id) return;
    setAvailabilityLoading(true);
    try {
      const response = await getAvailabilityByUserId(user.id);
      const raw = response?.data?.data ?? response?.data;
      const list = Array.isArray(raw) ? raw : [];
      setAvailabilityList(list.filter((a) => a?.is_active !== false));
    } catch (error) {
      console.error("Error fetching mentor availability:", error);
      setAvailabilityList([]);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [isMentor, user?.id]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const stats = [
    { label: "Session Scheduled", value: 0, icon: "lucide:trending-up" },
    { label: "Sessions Completed", value: 0, icon: "lucide:trending-up" },
  ];

  const sessions = [
  ];

  const today = new Date();
  const formattedDate = `${today.getDate()} ${today.toLocaleString("default", {
    month: "long",
  })}, ${today.getFullYear()}`;

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8 text-left">
      {/* Header */}
      <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2 items-start">
          <Typography as="h1" className="text-2xl font-bold text-primary-900 text-left">
            Calendar
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600 text-left">
            Manage your availability and view booked mentoring sessions
          </Typography>
        </div>
        <div className="flex items-center gap-3">
          {isMentor && (
            <Button
              variant="secondary"
              size="medium"
              startIcon={<Icon icon="lucide:calendar-clock" className="h-5 w-5" />}
              onClick={() => setShowAvailabilityModal(true)}
            >
              Set Availability
            </Button>
          )}
         
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-3 justify-start">
        <div className="flex items-center gap-3">
          <Avatar
            src={user?.profile_picture_url}
            firstName={user?.first_name}
            lastName={user?.last_name}
            email={user?.email}
            alt="User"
            size="w-12 h-12"
            showBorder={true}
            className="border-2 border-slate-200"
          />
          <div className="flex flex-col items-start">
            <Typography as="p" className="text-sm font-semibold text-primary-900 text-left">
              This Month
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600 text-left">
              {formattedDate}
            </Typography>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stats.map((stat) => (
          <Card
            key={stat.label}
            className="border border-slate-200 shadow-none text-left"
            bodyClassName="p-6 flex items-center gap-4 justify-start"
          >
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
              <Icon icon={stat.icon} className="h-5 w-5 text-slate-500" />
            </div>
            <div className="flex flex-col items-start">
              <Typography as="p" className="text-sm text-tertiary-600 text-left">
                {stat.label}
              </Typography>
              <Typography as="h3" className="text-2xl font-semibold text-primary-900 text-left">
                {stat.value}
              </Typography>
            </div>
          </Card>
        ))}
      </div>

      {/* Mentor Availability - only for mentors */}
      {isMentor && (
        <div className="flex flex-col gap-3 items-start w-full">
          <Typography as="h3" className="text-base font-semibold text-primary-900 text-left">
            Mentor Availability
          </Typography>
          {availabilityLoading ? (
            <Typography as="p" className="text-sm text-tertiary-600 text-left">
              Loading availability...
            </Typography>
          ) : availabilityList.length === 0 ? (
            <Typography as="p" className="text-sm text-tertiary-600 text-left">
              No availability set. Use &quot;Set Availability&quot; to add your schedule.
            </Typography>
          ) : (
            <div className="flex flex-col gap-3 w-full items-stretch">
              {availabilityList.map((av) => (
                <Card
                  key={av.id}
                  className="border border-slate-200 shadow-none text-left"
                  bodyClassName="p-4 flex flex-col gap-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-primary-900">
                      From {formatDate(av.start_date)}
                    </span>
                    {av.total_weeks != null && (
                      <span className="text-xs text-tertiary-600">
                        · {av.total_weeks} week{av.total_weeks !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {av.slots && av.slots.length > 0 ? (
                    <ul className="list-none p-0 m-0 flex flex-col gap-1.5">
                      {av.slots.map((slot) => (
                        <li
                          key={slot.id}
                          className="text-sm text-tertiary-700 flex items-center gap-2"
                        >
                          <Icon icon="lucide:clock" className="h-4 w-4 text-slate-500 shrink-0" />
                          <span>
                            {DAY_NAMES[slot.day_of_week] ?? `Day ${slot.day_of_week}`} ·{" "}
                            {formatTime(slot.from_time)} – {formatTime(slot.to_time)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography as="p" className="text-xs text-tertiary-600 text-left">
                      No slots in this period.
                    </Typography>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upcoming Sessions */}
      <div className="flex flex-col gap-3 items-start w-full">
        <Typography as="h3" className="text-base font-semibold text-primary-900 text-left">
          Upcoming Sessions
        </Typography>
        <div className="flex flex-col gap-3 w-full items-stretch">
          {sessions.map((session) => (
            <Card
              key={session.id}
              className="border border-slate-200 shadow-none"
              bodyClassName="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Icon icon={session.icon} className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex flex-col">
                  <Typography as="p" className="text-sm font-semibold text-primary-900">
                    {session.title}
                  </Typography>
                  <Typography as="p" className="text-xs text-tertiary-600">
                    {session.time}
                  </Typography>
                </div>
              </div>
              <Button
                variant="secondary"
                size="small"
                endIcon={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
              >
                Join Meeting
              </Button>
            </Card>
          ))}
        </div>
      </div>

      {/* Set Availability Modal */}
      {isMentor && (
        <SetAvailabilityModal
          isOpen={showAvailabilityModal}
          onClose={() => {
            setShowAvailabilityModal(false);
            fetchAvailability();
          }}
        />
      )}
    </div>
  );
};

export default Component;

